import { useState, useEffect } from 'react';
import {
  Alert, AutoComplete, Button, Card, Col, Form, Input, Modal, Row, Select, Space,
  Tabs, Typography, message, Tag, List, Popconfirm, Switch,
} from 'antd';
import {
  SyncOutlined, PlusOutlined, DeleteOutlined, EditOutlined,
  ThunderboltOutlined, ApiOutlined, AudioOutlined, FileTextOutlined, PictureOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import './Settings.css';

import { authApi } from '../../services/authService';
import { useAuthStore } from '../../stores/authStore';

const { Text } = Typography;

const LLM_DEFAULTS: Record<string, { baseUrl: string; models: string[] }> = {
  deepseek: { baseUrl: 'https://api.deepseek.com/v1', models: ['deepseek-chat', 'deepseek-reasoner', 'deepseek-v4-pro'] },
  glm: { baseUrl: 'https://open.bigmodel.cn/api/paas/v4', models: ['glm-5.1', 'glm-4.7', 'glm-4.5', 'glm-4-plus', 'glm-4-flash'] },
  gemini: { baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai', models: ['gemini-3-flash-preview', 'gemini-3.1-pro-preview'] },
  qwen: { baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1', models: ['qwen-max', 'qwen-plus'] },
  ernie: { baseUrl: 'https://qianfan.baidubce.com/v2', models: ['ernie-4.0-8k-latest'] },
  hunyuan: { baseUrl: 'https://api.hunyuan.cloud.tencent.com/v1', models: ['hunyuan-pro'] },
  kimi: { baseUrl: 'https://api.kimi.com/coding/v1', models: ['k3', 'k3-256k'] },
  openai: { baseUrl: 'https://api.openai.com/v1', models: ['gpt-4o', 'gpt-4.1'] },
  anthropic: { baseUrl: 'https://api.anthropic.com/v1', models: ['claude-sonnet-4-20250514', 'claude-opus-4-20250514', 'claude-opus-4-7'] },
  openai_compatible: { baseUrl: '', models: [] },
};

const PROVIDER_OPTIONS = Object.entries(LLM_DEFAULTS).map(([code]) => ({
  value: code,
  label: code === 'deepseek' ? 'DeepSeek' : code === 'glm' ? '智谱 GLM' :
    code === 'gemini' ? 'Google Gemini' :
    code === 'qwen' ? '通义千问' : code === 'ernie' ? '文心一言' :
    code === 'hunyuan' ? '腾讯混元' : code === 'kimi' ? 'Kimi Code' :
    code === 'openai' ? 'OpenAI' : code === 'openai_compatible' ? 'OpenAI Compatible' : 'Anthropic Claude',
}));

const SCENES = [
  { key: 'kb-dedup', title: '📚 知识库上传 · AI去重', desc: '批量上传文档时识别重复/旧版本,只保留最新版' },
  { key: 'api-doc', title: '🔌 接口文档导入 / 解析', desc: '把 URL 或文件里的接口内容解析成结构化 API 文档' },
  { key: 'work-intel-refine', title: '🔍 工作情报 · 提炼研判', desc: '个人工作情报的聊天记录提炼、联系人画像研判' },
  { key: 'ai-command-office', title: '🏢 AI 指挥中心 · 相关任务', desc: '配置 AI 指挥办公室口头汇报、文档和表格任务所使用的大模型' },
];

interface Provider { id: string; name: string; code: string; baseUrl?: string; apiKey?: string; }
interface Model { id: string; name: string; code: string; providerId: string; maxTokens: number; }

function RealtimeSpeechConfigCard() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // 加载当前配置
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/ai-demand-center/system/speech-config');
        if (res.data) {
          form.setFieldsValue({
            appId: res.data.appId || '',
            apiSecret: res.data.apiSecret || '',
            apiKey: res.data.apiKey || '',
          });
        }
      } catch { /* 非 admin 无权限，静默忽略 */ }
      finally { setFetching(false); }
    })();
  }, [form]);

  const handleSave = async (values: { appId: string; apiSecret: string; apiKey: string }) => {
    setLoading(true);
    try {
      await api.put('/ai-demand-center/system/speech-config', values);
      message.success('实时语音配置已保存，热生效无需重启');
    } catch (e: any) {
      message.error(e.response?.data?.message || '保存失败');
    } finally { setLoading(false); }
  };

  return (
    <Card title={<><AudioOutlined /> 实时语音模型配置</>} loading={fetching}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="配置讯飞实时语音转写大模型的 APPID / APISecret / APIKey。保存后即时生效，无需重启服务。每账号赠送 5 小时额度，用完后可在此更换凭据。" />
      <Form form={form} layout="vertical" onFinish={handleSave} style={{ maxWidth: 480 }}>
        <Form.Item name="appId" label="APPID" rules={[{ required: true, message: '请输入 APPID' }]}>
          <Input placeholder="讯飞控制台→实时语音转写→APPID" />
        </Form.Item>
        <Form.Item name="apiSecret" label="APISecret" rules={[{ required: true, message: '请输入 APISecret' }]}>
          <Input.Password placeholder="讯飞控制台→实时语音转写→APISecret" />
        </Form.Item>
        <Form.Item name="apiKey" label="APIKey" rules={[{ required: true, message: '请输入 APIKey' }]}>
          <Input.Password placeholder="讯飞控制台→实时语音转写→APIKey" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>保存配置</Button>
      </Form>
    </Card>
  );
}


function TtsSpeechConfigCard() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/ai-demand-center/system/tts-config');
        form.setFieldsValue({
          appId: res.data?.appId || '',
          apiSecret: res.data?.apiSecret || '',
          apiKey: res.data?.apiKey || '',
          voice: res.data?.voice || 'aisbabyxu',
          speed: res.data?.speed ?? 50,
          volume: res.data?.volume ?? 70,
          pitch: res.data?.pitch ?? 55,
        });
      } catch { /* 非 admin 无权限，静默忽略 */ }
      finally { setFetching(false); }
    })();
  }, [form]);

  const handleSave = async (values: { appId: string; apiSecret: string; apiKey: string; voice?: string; speed?: number; volume?: number; pitch?: number }) => {
    setLoading(true);
    try {
      await api.put('/ai-demand-center/system/tts-config', values);
      message.success('语音合成配置已保存，小灵播报立即生效');
    } catch (e: any) {
      message.error(e.response?.data?.message || '保存失败');
    } finally { setLoading(false); }
  };

  return (
    <Card title={<><AudioOutlined /> 讯飞语音合成配置</>} loading={fetching}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="配置讯飞在线语音合成 TTS，用于小灵短句播报。密钥仅保存在后端，前端只调用平台接口播放音频。" />
      <Form form={form} layout="vertical" onFinish={handleSave} style={{ maxWidth: 560 }}>
        <Form.Item name="appId" label="APPID" rules={[{ required: true, message: '请输入 APPID' }]}>
          <Input placeholder="讯飞控制台→在线语音合成→APPID" />
        </Form.Item>
        <Form.Item name="apiSecret" label="APISecret" rules={[{ required: true, message: '请输入 APISecret' }]}>
          <Input.Password placeholder="讯飞控制台→在线语音合成→APISecret" />
        </Form.Item>
        <Form.Item name="apiKey" label="APIKey" rules={[{ required: true, message: '请输入 APIKey' }]}>
          <Input.Password placeholder="讯飞控制台→在线语音合成→APIKey" />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="voice" label="发音人">
              <Input placeholder="默认 aisbabyxu（更年轻可爱）" />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item name="speed" label="语速">
              <Input type="number" min={0} max={100} />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item name="volume" label="音量">
              <Input type="number" min={0} max={100} />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item name="pitch" label="音调">
              <Input type="number" min={0} max={100} />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" loading={loading}>保存语音合成配置</Button>
      </Form>
    </Card>
  );
}

/** 图片生成模型配置（设计图生成 + PPT 页面生图共用，按调用优先级排列通道，保存即生效） */
function ImageGenConfigCard() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/ai-demand-center/system/image-gen-config');
        if (res.data) form.setFieldsValue(res.data);
      } catch { /* 非 admin 无权限，静默忽略 */ }
      finally { setFetching(false); }
    })();
  }, [form]);

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      await api.put('/ai-demand-center/system/image-gen-config', values);
      message.success('图片生成模型配置已保存，下一次生图立即生效');
    } catch (e: any) {
      message.error(e.response?.data?.message || '保存失败');
    } finally { setLoading(false); }
  };

  const kv = (name: (string | number)[], label: string, placeholder: string, password = false) => (
    <Form.Item key={name.join('.')} name={name} label={label} style={{ marginBottom: 12 }}>
      {password ? <Input.Password placeholder={placeholder} autoComplete="new-password" /> : <Input placeholder={placeholder} />}
    </Form.Item>
  );

  return (
    <Card title={<><PictureOutlined /> 图片生成模型配置</>} loading={fetching}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="设计图生成、PPT 页面生图共用以下通道，按顺序自动回退：通道1 失败自动切通道2，再失败切通道3。某个通道留空 API Key 则跳过该通道。保存后下一次生图立即生效，无需重启。密钥仅保存在服务端数据库。" />
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Card type="inner" title="通道1（优先）· Apinebula GPT" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col xs={24} md={12}>{kv(['apinebulaGpt', 'apiKey'], 'API Key', 'Apinebula GPT 生图 Key', true)}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGpt', 'model'], '模型', '默认 gpt-image-2')}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGpt', 'generationUrl'], '生图接口地址', 'https://apinebula.com/v1/images/generations')}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGpt', 'editUrl'], '图片编辑接口地址', 'https://apinebula.com/v1/images/edits')}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGpt', 'quality'], '画质', '默认 low（可选 low/medium/high）')}</Col>
          </Row>
        </Card>
        <Card type="inner" title="通道2 · API.B" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col xs={24} md={12}>{kv(['apib', 'apiKey'], 'API Key', 'API.B 生图 Key', true)}</Col>
            <Col xs={24} md={12}>{kv(['apib', 'model'], '模型', '默认 gpt-image-2-official')}</Col>
            <Col xs={24} md={12}>{kv(['apib', 'generationUrl'], '生图接口地址', 'https://api.apib.ai/v1/images/generations')}</Col>
            <Col xs={24} md={6}>{kv(['apib', 'resolution'], '分辨率', '默认 1k')}</Col>
            <Col xs={24} md={6}>{kv(['apib', 'quality'], '画质', '默认 auto')}</Col>
            <Col xs={24} md={6}>{kv(['apib', 'outputFormat'], '输出格式', '默认 png')}</Col>
          </Row>
        </Card>
        <Card type="inner" title="通道3（兜底）· img-api" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col xs={24} md={12}>{kv(['legacy', 'apiKey'], 'API Key', 'img-api 生图 Key', true)}</Col>
            <Col xs={24} md={12}>{kv(['legacy', 'model'], '模型', '默认 gpt-image-2')}</Col>
            <Col xs={24} md={12}>{kv(['legacy', 'baseUrl'], '服务地址', 'https://img-api.apinebula.com')}</Col>
            <Col xs={24} md={12}>{kv(['legacy', 'quality'], '画质', '默认 high')}</Col>
          </Row>
        </Card>
        <Card type="inner" title="备用 · Apinebula GPT（Key2，仅在生图页手动指定模型时使用）" style={{ marginBottom: 16 }}>
          <Row gutter={12}>
            <Col xs={24} md={12}>{kv(['apinebulaGptAlt', 'apiKey'], 'API Key', '备用 Key（可空）', true)}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGptAlt', 'model'], '模型', '默认 gpt-image-2')}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGptAlt', 'generationUrl'], '生图接口地址（可空，空则沿用通道1）', '')}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGptAlt', 'editUrl'], '图片编辑接口地址（可空，空则沿用通道1）', '')}</Col>
            <Col xs={24} md={12}>{kv(['apinebulaGptAlt', 'quality'], '画质', '默认 high')}</Col>
          </Row>
        </Card>
        <Button type="primary" htmlType="submit" loading={loading}>保存图片生成模型配置</Button>
      </Form>
    </Card>
  );
}

function OcrConfigCard() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/ocr/config');
        form.setFieldsValue({
          appId: res.data?.appId || '',
          apiSecret: res.data?.apiSecret || '',
          enabled: res.data?.enabled !== false,
        });
      } catch { /* 非 admin 无权限，静默忽略 */ }
      finally { setFetching(false); }
    })();
  }, [form]);

  const handleSave = async (values: { appId: string; apiSecret: string; enabled?: boolean }) => {
    setLoading(true);
    try {
      await api.put('/ocr/config', values);
      message.success('讯飞PDF OCR配置已保存，立即生效');
    } catch (e: any) {
      message.error(e.response?.data?.message || '保存失败');
    } finally { setLoading(false); }
  };

  return (
    <Card title={<><FileTextOutlined /> 讯飞 PDF OCR 配置</>} loading={fetching}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="配置讯飞 PDF文档识别（OCR大模型）。当知识库上传或智能工坊附件遇到「图片型/扫描件 PDF」（无文字层）时，自动调用讯飞 OCR 识别为文本。密钥仅保存在后端。" />
      <Form form={form} layout="vertical" onFinish={handleSave} style={{ maxWidth: 560 }}>
        <Form.Item name="appId" label="APPID" rules={[{ required: true, message: '请输入 APPID' }]}>
          <Input placeholder="讯飞控制台→PDF文档识别→APPID" />
        </Form.Item>
        <Form.Item name="apiSecret" label="APISecret" rules={[{ required: true, message: '请输入 APISecret' }]}>
          <Input.Password placeholder="讯飞控制台→PDF文档识别→APISecret" />
        </Form.Item>
        <Form.Item name="enabled" label="启用 OCR" valuePropName="checked" initialValue={true}>
          <Switch checkedChildren="开" unCheckedChildren="关" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>保存 OCR 配置</Button>
      </Form>
    </Card>
  );
}

function OcrImageConfigCard() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/ocr/image-config');
        form.setFieldsValue({
          appId: res.data?.appId || '',
          apiKey: res.data?.apiKey || '',
          apiSecret: res.data?.apiSecret || '',
          enabled: res.data?.enabled !== false,
        });
      } catch { /* 非 admin 无权限，静默忽略 */ }
      finally { setFetching(false); }
    })();
  }, [form]);

  const handleSave = async (values: { appId: string; apiKey: string; apiSecret: string; enabled?: boolean }) => {
    setLoading(true);
    try {
      await api.put('/ocr/image-config', values);
      message.success('讯飞图片识别配置已保存，立即生效');
    } catch (e: any) {
      message.error(e.response?.data?.message || '保存失败');
    } finally { setLoading(false); }
  };

  return (
    <Card title={<><FileTextOutlined /> 讯飞 图片识别（通用文字识别）配置</>} loading={fetching}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="配置讯飞通用文字识别，用于智能工坊「图片附件」的文字提取（jpg/png/bmp，单图≤4MB）。密钥仅保存在后端。" />
      <Form form={form} layout="vertical" onFinish={handleSave} style={{ maxWidth: 560 }}>
        <Form.Item name="appId" label="APPID" rules={[{ required: true, message: '请输入 APPID' }]}>
          <Input placeholder="讯飞控制台→通用文字识别→APPID" />
        </Form.Item>
        <Form.Item name="apiKey" label="APIKey" rules={[{ required: true, message: '请输入 APIKey' }]}>
          <Input.Password placeholder="讯飞控制台→通用文字识别→APIKey" />
        </Form.Item>
        <Form.Item name="apiSecret" label="APISecret" rules={[{ required: true, message: '请输入 APISecret' }]}>
          <Input.Password placeholder="讯飞控制台→通用文字识别→APISecret" />
        </Form.Item>
        <Form.Item name="enabled" label="启用图片识别" valuePropName="checked" initialValue={true}>
          <Switch checkedChildren="开" unCheckedChildren="关" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>保存图片识别配置</Button>
      </Form>
    </Card>
  );
}

function ChangePasswordCard() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const handleChangePwd = async (values: { oldPassword: string; newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的新密码不一致');
      return;
    }
    if (values.newPassword.length < 6) {
      message.error('新密码长度不能少于6位');
      return;
    }
    setLoading(true);
    try {
      await authApi.changePassword(values.oldPassword, values.newPassword);
      message.success('密码修改成功，请重新登录');
      form.resetFields();
      // 强制退出，重新登录
      setTimeout(() => {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }, 1500);
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '修改失败');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Card>
      <Form form={form} layout="vertical" onFinish={handleChangePwd} style={{ maxWidth: 400 }}>
        <Form.Item name="oldPassword" label="原密码" rules={[{ required: true, message: '请输入原密码' }]}>
          <Input.Password placeholder="输入当前密码" />
        </Form.Item>
        <Form.Item name="newPassword" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '至少6位' }]}>
          <Input.Password placeholder="输入新密码（至少6位）" />
        </Form.Item>
        <Form.Item name="confirmPassword" label="确认新密码" rules={[{ required: true, message: '请再次输入新密码' }]}>
          <Input.Password placeholder="再次输入新密码" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>修改密码</Button>
      </Form>
    </Card>
  );
}

export default function Settings() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [prefs, setPrefs] = useState<Record<string, string>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editPid, setEditPid] = useState<string | null>(null);
  const [editMid, setEditMid] = useState<string | null>(null);
  const [selCode, setSelCode] = useState('deepseek');
  const [testStatus, setTestStatus] = useState<'idle'|'testing'|'ok'|'fail'>('idle');
  const [detecting, setDetecting] = useState(false);
  const [detectResult, setDetectResult] = useState<any>(null);
  const [detectOpen, setDetectOpen] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [fetchingModels, setFetchingModels] = useState(false);
  const [fetchedModels, setFetchedModels] = useState<string[]>([]);
  const [form] = Form.useForm();

  const loadData = async () => {
    try {
      const [pRes, mRes, prefRes] = await Promise.all([
        api.get('/llm/providers'), api.get('/llm/models'), api.get('/llm/preferences'),
      ]);
      setProviders(pRes.data || []); setModels(mRes.data || []);
      const p: Record<string, string> = {};
      (prefRes.data || []).forEach((x: any) => { p[x.taskType] = x.modelId; });
      setPrefs(p);
    } catch (e: any) {
      console.error('加载模型配置失败:', e.response?.status, e.response?.data?.message || e.message);
    }
  };
  useEffect(() => { loadData(); }, []);

  const openAdd = () => {
    setEditPid(null); setEditMid(null); setSelCode('deepseek'); setTestStatus('idle'); setTestMsg('');
    setFetchedModels([]);
    form.resetFields();
    form.setFieldsValue({ code: 'deepseek', baseUrl: LLM_DEFAULTS.deepseek.baseUrl, model: LLM_DEFAULTS.deepseek.models[0], maxTokens: 65535 });
    setModalOpen(true);
  };

  const openEdit = (pid: string, mid: string) => {
    const p = providers.find(x => x.id === pid);
    const m = models.find(x => x.id === mid);
    if (!p || !m) return;
    setEditPid(pid); setEditMid(mid); setSelCode(p.code); setTestStatus('idle'); setTestMsg('');
    setFetchedModels([]);
    form.setFieldsValue({ code: p.code, baseUrl: p.baseUrl, apiKey: p.apiKey || '', model: m.code, maxTokens: m.maxTokens });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      let pid = editPid;
      if (pid) {
        const upd: any = { baseUrl: values.baseUrl };
        // 编辑也允许更换厂商：厂商代码/名称随下拉选择同步更新
        const prev = providers.find(x => x.id === pid);
        if (values.code && values.code !== prev?.code) {
          upd.code = values.code;
          upd.name = PROVIDER_OPTIONS.find(x => x.value === values.code)?.label || values.code;
        }
        if (values.apiKey) upd.apiKey = values.apiKey;
        await api.put(`/llm/providers/${pid}`, upd);
      } else {
        // 每次添加都创建新提供商，允许同协议多配置（不同key/url）
        const pres = await api.post('/llm/providers', {
          name: PROVIDER_OPTIONS.find(x => x.value === values.code)?.label || values.code,
          code: values.code, type: 'custom', baseUrl: values.baseUrl, apiKey: values.apiKey,
        });
        pid = pres.data.id;
      }
      if (editMid) {
        await api.put(`/llm/models/${editMid}`, { name: values.model, code: values.model, maxTokens: values.maxTokens || 65536 });
      } else {
        await api.post('/llm/models', { name: values.model, code: values.model, type: 'chat', providerId: pid, maxTokens: values.maxTokens || 65536 });
      }
      message.success('已保存');
      setModalOpen(false);
      loadData();
    } catch (e: any) {
      if (e.errorFields) return;
      const errMsg = e.response?.data?.message || e.message || '保存失败';
      console.error('保存模型配置失败:', e.response?.status, errMsg);
      message.error(errMsg);
    } finally { setSaving(false); }
  };

  const handleDelete = async (pid: string, mid: string) => {
    try {
      await api.delete(`/llm/models/${mid}`);
      // 删完模型后检查：如果该提供商已无模型，则连带删除提供商
      const mRes = await api.get('/llm/models');
      const remaining = (mRes.data || []).filter((m: any) => m.providerId === pid);
      if (remaining.length === 0) {
        await api.delete(`/llm/providers/${pid}`);
      }
      message.success('已删除'); loadData();
    } catch (e: any) { message.error(e.response?.data?.message || '删除失败，请刷新后重试'); }
  };

  const handleTest = async () => {
    const values = form.getFieldsValue();
    let apiKey = values.apiKey || '';
    // 编辑时如果没填 Key，尝试用已保存的 Key
    if (!apiKey && editPid) {
      const p = providers.find(x => x.id === editPid);
      apiKey = p?.apiKey || '';
    }
    if (!apiKey) { message.warning('请填写 API Key'); return; }
    setTestStatus('testing'); setTestMsg('');
    try {
      const res = await api.post('/llm/test', { provider: values.code, apiKey, baseUrl: values.baseUrl, model: values.model });
      if (res.data?.success) { setTestStatus('ok'); setTestMsg(res.data.message); }
      else { setTestStatus('fail'); setTestMsg(res.data?.error || '连接失败'); }
    } catch (e: any) { setTestStatus('fail'); setTestMsg(e.response?.data?.message || '测试失败'); }
  };

  const handleFetchModels = async () => {
    const values = form.getFieldsValue();
    const apiKey = values.apiKey || '';
    const baseUrl = (values.baseUrl || '').replace(/\/+$/, '');
    if (!apiKey) { message.warning('请先填写 API Key'); return; }
    if (!baseUrl) { message.warning('请先填写 API 地址'); return; }
    setFetchingModels(true);
    try {
      const res = await api.post('/llm/fetch-models', { apiKey, baseUrl });
      if (res.data?.success === false) {
        message.error(`获取失败: ${res.data.error}`);
        setFetchedModels([]);
        return;
      }
      const names: string[] = (res.data?.models || res.data?.data || [])
        .filter((n: string) => n && !n.includes('embedding') && !n.includes('moderation') && !n.includes('whisper') && !n.includes('tts') && !n.includes('dall-e') && !n.includes('dalle'))
        .sort();
      if (names.length === 0) { message.warning('未获取到可用模型'); setFetchedModels([]); }
      else { message.success(`获取到 ${names.length} 个模型`); setFetchedModels(names); }
    } catch (e: any) {
      message.error(`获取失败: ${e.response?.data?.error || e.message}`);
      setFetchedModels([]);
    } finally { setFetchingModels(false); }
  };

  const handleAssign = async (sceneKey: string, modelId: string) => {
    try {
      await api.post('/llm/preferences', { taskType: sceneKey, modelId });
      setPrefs(p => ({ ...p, [sceneKey]: modelId }));
      message.success('已设置');
    } catch { message.error('设置失败'); }
  };

  const [detectModelId, setDetectModelId] = useState<string>('');

  const handleDetectModel = async () => {
    if (llmList.length === 0) { message.warning('请先配置一个模型'); return; }
    const selected = detectModelId
      ? llmList.find(x => x.mid === detectModelId)
      : llmList[0];
    if (!selected) { message.warning('未找到选中的模型'); return; }
    const p = selected.provider;
    const m = selected.model;
    setDetecting(true);
    setDetectOpen(true);
    setDetectResult(null);

    const rounds: any[] = [];
    for (let rnd = 1; rnd <= 1; rnd++) {
      setDetectResult({ rounds: [...rounds], progress: `检测中...`, modelConfigured: m.code, providerName: p.name });
      try {
        const res = await api.post('/llm/detect-model-v2', {
          baseUrl: p.baseUrl || '',
          apiKey: p.apiKey || '',
          model: m.code,
        }, { timeout: 120000 });
        const data = res.data;
        rounds.push({
          round: rnd,
          totalTimeMs: data.totalTimeMs,
          consensus: data.consensus,
          confidence: data.confidence,
          detail: data.detail || '',
          results: (data.results || []).map((r: any) => ({
            type: r.type,
            label: r.label,
            prompt: r.prompt || '',
            value: r.value || r.inferred || '',
            rawResponse: r.rawResponse || '',
            error: r.error,
            reliable: r.reliable,
            latencyMs: r.latencyMs,
          })),
        });
        setDetectResult({ rounds: [...rounds], progress: `检测完成`, modelConfigured: m.code, providerName: p.name });
      } catch (e: any) {
        rounds.push({
          round: rnd,
          error: e.response?.data?.message || e.message || '检测失败',
          totalTimeMs: 0,
          consensus: '错误',
          confidence: '无',
          results: [],
        });
        setDetectResult({ rounds: [...rounds], progress: `检测失败`, modelConfigured: m.code, providerName: p.name });
      }
    }
    setDetectResult({ rounds, progress: '完成', modelConfigured: m.code, providerName: p.name });
    setDetecting(false);
  };

  const handleSyncAll = async () => {
    if (models.length === 0) return;
    try {
      for (const s of SCENES) await api.post('/llm/preferences', { taskType: s.key, modelId: models[0].id });
      message.success('已同步'); loadData();
    } catch { message.error('同步失败'); }
  };

  const modelOptions = models.map(m => {
    const p = providers.find(x => x.id === m.providerId);
    return { value: m.id, label: `${p?.name || '?'} / ${m.name}` };
  });

  const llmList: { pid: string; mid: string; provider: Provider; model: Model }[] = [];
  for (const p of providers) {
    for (const m of models.filter(x => x.providerId === p.id)) {
      llmList.push({ pid: p.id, mid: m.id, provider: p, model: m });
    }
  }

  const { user } = useAuthStore();
  const isAdmin = user?.roles?.includes('admin');

  return (
    <div>
      <Tabs items={[
        { key: 'profile', label: '个人资料', children: <Card><Form layout="vertical"><Form.Item name="name" label="姓名"><Input /></Form.Item><Form.Item name="email" label="邮箱"><Input disabled /></Form.Item><Button type="primary" htmlType="submit">保存</Button></Form></Card> },
        {
          key: 'password', label: '🔒 修改密码',
          children: <ChangePasswordCard />,
        },
        {
          key: 'llm', label: '大模型配置',
          children: (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card title={<><ThunderboltOutlined /> 模型配置管理</>}
                extra={<Space>
                  <Select size="small" placeholder="选择模型" value={detectModelId || undefined}
                    style={{ width: 200 }} allowClear
                    onChange={v => setDetectModelId(v || '')}
                    options={llmList.map(e => ({ value: e.mid, label: `${e.provider.name} / ${e.model.name}` }))} />
                  <Button onClick={handleDetectModel} loading={detecting} icon={<ThunderboltOutlined />}>检测真实模型</Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>添加模型</Button>
                </Space>}>
                {llmList.length === 0 ? <Alert type="warning" message="尚未配置大模型，请点击「添加模型」" /> : (
                  <List dataSource={llmList} renderItem={e => (
                    <Card size="small" style={{ marginBottom: 8 }}
                      extra={<Space><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(e.pid, e.mid)} /><Popconfirm title="确认删除？" onConfirm={() => handleDelete(e.pid, e.mid)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm></Space>}>
                      <Space><Tag color="blue">{e.provider.name}</Tag><Text code>{e.model.name}</Text><Text type="secondary" style={{ fontSize: 12 }}>{e.provider.baseUrl}</Text></Space>
                    </Card>
                  )} />
                )}
              </Card>
              {llmList.length > 0 && (
                <Card title="分环节应用模型" extra={<Button icon={<SyncOutlined />} onClick={handleSyncAll}>一键同步到所有环节</Button>}>
                  <Alert type="info" showIcon style={{ marginBottom: 16 }} message="为各业务环节分别指定使用的模型" />
                  <Row gutter={[16, 16]}>
                    {SCENES.map(s => (
                      <Col xs={24} md={12} key={s.key}>
                        <Card size="small" title={s.title}>
                          <Text type="secondary" style={{ fontSize: 12 }}>{s.desc}</Text>
                          <div style={{ marginTop: 8 }}>
                            <Select style={{ width: '100%' }} value={prefs[s.key]} placeholder="选择模型"
                              options={modelOptions} onChange={v => handleAssign(s.key, v)} />
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}
            </Space>
          ),
        },
        ...(isAdmin ? [{
          key: 'speech' as const,
          label: (<><AudioOutlined /> 实时语音模型配置</>),
          children: <RealtimeSpeechConfigCard />,
        }, {
          key: 'tts' as const,
          label: (<><AudioOutlined /> 语音合成配置</>),
          children: <TtsSpeechConfigCard />,
        }, {
          key: 'imagegen' as const,
          label: (<><PictureOutlined /> 图片生成模型配置</>),
          children: <ImageGenConfigCard />,
        }, {
          key: 'ocr' as const,
          label: (<><FileTextOutlined /> PDF OCR 配置</>),
          children: <OcrConfigCard />,
        }, {
          key: 'imgocr' as const,
          label: (<><FileTextOutlined /> 图片识别配置</>),
          children: <OcrImageConfigCard />,
        }] : []),
      ]} />
      <Modal className="settings-model-modal" title={null} open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>取消</Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>保存配置</Button>,
        ]}
        width={780}>
        <div className="df-form-modal-head">
          <div>
            <span className="df-form-modal-kicker">大模型配置</span>
            <h2>{editPid ? '编辑模型配置' : '添加模型配置'}</h2>
            <p>按“厂商与接口 → 密钥与模型 → 连接测试”三步填写，所有输入框统一宽度、统一边框，避免信息挤在一起。</p>
          </div>
          <span className="df-form-modal-icon"><ThunderboltOutlined /></span>
        </div>
        <Form className="df-form-modal-body" form={form} layout="vertical">
          <section className="df-form-section">
            <div className="df-form-section-head">
              <div>
                <span className="df-form-section-step">01</span>
                <strong>厂商与接口</strong>
              </div>
              <p>先选择模型厂商，系统会自动带出常用 API 地址和默认模型。</p>
            </div>
            <div className="df-form-grid df-form-grid-two">
              <Form.Item name="code" label="模型厂商" rules={[{ required: true, message: '请选择模型厂商' }]}>
                <Select popupClassName="settings-model-select-popup" options={PROVIDER_OPTIONS} onChange={(v: string) => { setSelCode(v); setFetchedModels([]); form.setFieldsValue({ baseUrl: LLM_DEFAULTS[v]?.baseUrl || '', model: LLM_DEFAULTS[v]?.models[0] || '' }); }} />
              </Form.Item>
              <Form.Item name="maxTokens" label="最大 Token">
                <Input type="number" placeholder="例如 8192" />
              </Form.Item>
            </div>
            <Form.Item name="baseUrl" label="API 地址" rules={[{ required: true, message: '请输入 API 地址' }]}>
              <Input placeholder="https://api.example.com/v1" />
            </Form.Item>
          </section>

          <section className="df-form-section">
            <div className="df-form-section-head">
              <div>
                <span className="df-form-section-step">02</span>
                <strong>密钥与模型</strong>
              </div>
              <p>API Key 与模型名称放在同一组内，保持同一宽度、同一高度、同一视觉层级。</p>
            </div>
            <div className="df-form-grid df-form-grid-two df-form-grid-equal">
              <Form.Item name="apiKey" label="API Key" rules={editPid ? [] : [{ required: true, message: '请输入 API Key' }]} extra={editPid ? '留空则不修改已保存的 Key' : ''}>
                <Input.Password placeholder="sk-..." />
              </Form.Item>
              <Form.Item name="model" label="模型名称" rules={[{ required: true, message: '请选择或输入模型名称' }]}>
                <AutoComplete
                  popupClassName="settings-model-select-popup"
                  placeholder="选择或手动输入模型名称"
                  options={(selCode === 'openai_compatible' ? fetchedModels : (LLM_DEFAULTS[selCode]?.models || [])).map(m => ({ value: m, label: m }))}
                  disabled={false}
                  filterOption={(inputValue, option) =>
                    option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
              </Form.Item>
            </div>
            {selCode === 'openai_compatible' && (
              <div className="df-form-inline-action">
                <Button icon={<ApiOutlined />} onClick={handleFetchModels} loading={fetchingModels}>
                  {fetchedModels.length > 0 ? `已获取 ${fetchedModels.length} 个模型` : '从 API 地址获取模型列表'}
                </Button>
              </div>
            )}
          </section>

          <section className="df-form-section df-form-section-test">
            <div className="df-form-section-head">
              <div>
                <span className="df-form-section-step">03</span>
                <strong>连接测试</strong>
              </div>
              <p>保存前建议先测试，避免模型地址或密钥填错后影响 AI 全流程。</p>
            </div>
            <Button className="df-form-test-button" block icon={<ApiOutlined />} onClick={handleTest} loading={testStatus === 'testing'}
              type={testStatus === 'ok' ? 'default' : 'dashed'}>
              {testStatus === 'testing' ? '测试中...' : testStatus === 'ok' ? '连接成功' : testStatus === 'fail' ? '重新测试' : '测试连接'}
            </Button>
            {testMsg && <Alert type={testStatus === 'ok' ? 'success' : 'error'} message={testMsg} />}
          </section>
        </Form>
      </Modal>

      {/* 模型检测结果弹窗 */}
      <Modal title="🔍 真实模型检测" open={detectOpen} onCancel={() => setDetectOpen(false)}
        footer={<Button onClick={() => setDetectOpen(false)}>关闭</Button>} width={960}>
        {detecting ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <ThunderboltOutlined spin style={{ fontSize: 48, color: '#f5a623' }} />
            <div style={{ marginTop: 16, color: '#888' }}>{detectResult?.progress || '正在检测模型身份...'}</div>
          </div>
        ) : detectResult?.error ? (
          <Alert type="error" message={detectResult.error} />
        ) : detectResult?.rounds ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ background: '#faf6ed', padding: 12, borderRadius: 8, border: '1px solid #e0d5c0' }}>
              <Text strong>配置的模型: </Text><Tag color="blue">{detectResult.modelConfigured}</Tag>
              {detectResult.providerName && <Tag>{detectResult.providerName}</Tag>}
            </div>
            {detectResult.rounds.map((rnd: any, ri: number) => (
              <Card key={ri} size="small" title={
                <Space wrap>
                  <Tag color={rnd.error ? 'red' : rnd.confidence === '高' ? 'green' : rnd.confidence === '中' ? 'orange' : 'default'}>
                    第{rnd.round}轮
                  </Tag>
                  <Text strong>共识: {rnd.consensus || '—'}</Text>
                  <Tag>{rnd.confidence}置信度</Tag>
                  {rnd.detail && <Text type="secondary" style={{ fontSize: 11 }}>{rnd.detail}</Text>}
                  <Text type="secondary" style={{ fontSize: 11 }}>{(rnd.totalTimeMs / 1000).toFixed(1)}s</Text>
                </Space>
              }>
                {rnd.error ? (
                  <Alert type="error" message={rnd.error} />
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#faf6ed' }}>
                        <th style={{ padding: '4px 6px', border: '1px solid #e0d5c0', width: 150 }}>探测方法</th>
                        <th style={{ padding: '4px 6px', border: '1px solid #e0d5c0' }}>结果</th>
                        <th style={{ padding: '4px 6px', border: '1px solid #e0d5c0', textAlign: 'center', width: 60 }}>耗时</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rnd.results || []).map((r: any, i: number) => (
                        <tr key={i} style={{ background: r.error ? '#fff2f0' : r.reliable ? '#f6ffed' : i % 2 === 0 ? '#fff' : '#faf7ed' }}>
                          <td style={{ padding: '4px 6px', border: '1px solid #e0d5c0', verticalAlign: 'top' }}>
                            {r.reliable ? '✅ ' : r.error ? '❌ ' : '• '}
                            {r.label}
                          </td>
                          <td style={{ padding: '4px 6px', border: '1px solid #e0d5c0' }}>
                            {r.error ? (
                              <Tag color="red">{r.error}</Tag>
                            ) : (
                              <div>
                                <div style={{ marginBottom: 4 }}>
                                  <Text code style={{ fontSize: 11, background: '#f0f0f0' }}>{r.value || '(空)'}</Text>
                                </div>
                                {r.prompt && (
                                  <details style={{ marginTop: 4 }}>
                                    <summary style={{ cursor: 'pointer', color: '#888', fontSize: 11 }}>📤 原始请求</summary>
                                    <pre style={{ fontSize: 10, background: '#f5f5f5', padding: 6, borderRadius: 4, margin: '4px 0', maxHeight: 60, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{r.prompt}</pre>
                                  </details>
                                )}
                                {r.rawResponse && (
                                  <details style={{ marginTop: 2 }}>
                                    <summary style={{ cursor: 'pointer', color: '#888', fontSize: 11 }}>📥 原始LLM返回</summary>
                                    <pre style={{ fontSize: 9, background: '#f5f5f5', padding: 6, borderRadius: 4, margin: '4px 0', maxHeight: 150, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{r.rawResponse.length > 2000 ? r.rawResponse.slice(0, 2000) + '\n...(截断)' : r.rawResponse}</pre>
                                  </details>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '4px 6px', border: '1px solid #e0d5c0', textAlign: 'center', fontSize: 11 }}>{r.latencyMs}ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            ))}
          </Space>
        ) : null}
      </Modal>
    </div>
  );
}
